import { ReactNode, createContext, useContext, useMemo, useState } from 'react';
import { PackageQuote } from '@graphql';

interface PackageQuoteContextType {
  selectedQuotes: PackageQuote[];
  handleSelect: (quote: PackageQuote) => void;
  handleRemove: (quote: PackageQuote) => void;
  setSelectedQuotes: (quotes: PackageQuote[]) => void;
}

const PackageQuoteContext = createContext<PackageQuoteContextType | undefined>(undefined);

export const PackageQuoteProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedQuotes, setSelectedQuotes] = useState<PackageQuote[]>([]);

  const handleSelect = (quote: PackageQuote) => {
    setSelectedQuotes(prev => [...prev, quote]);
  };

  const handleRemove = (quote: PackageQuote) =>
    setSelectedQuotes(prev => prev.filter(q => q.trackingId !== quote.trackingId));

  const packageQuoteContext = useMemo(
    () => ({
      selectedQuotes,
      handleSelect,
      handleRemove,
      setSelectedQuotes,
    }),
    [selectedQuotes],
  );

  return <PackageQuoteContext.Provider value={packageQuoteContext}>{children}</PackageQuoteContext.Provider>;
};

export const usePackageQuoteContext = () => {
  const context = useContext(PackageQuoteContext);

  if (!context) {
    throw new Error('usePackageQuoteContext must be used within a PackageQuoteProvider');
  }

  return context;
};
